import React, { useEffect, useRef, useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import { trySetServiceUnavailableError } from '../../utils/serviceUnavailableMessages';

const Login = ({ onSwitchToRegister, onSwitchToForgotPassword }) => {
  const { login } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const visitStartedAtRef = useRef(null);
  const visitSessionIdRef = useRef(null);

  useEffect(() => {
    visitStartedAtRef.current = new Date();
    visitSessionIdRef.current = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    return () => {
      if (!visitStartedAtRef.current) return;
      const durationSeconds = Math.max(
        0,
        Math.round((Date.now() - visitStartedAtRef.current.getTime()) / 1000)
      );
      api.trackPageVisit(
        'login',
        visitSessionIdRef.current,
        durationSeconds,
        visitStartedAtRef.current.toISOString(),
      ).catch(() => {});
    };
  }, []);

  const handleAboutProjectClick = () => {
    window.history.pushState({}, '', '/landing');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password, formData.rememberMe);
    } catch (err) {
      if (trySetServiceUnavailableError(err, setError, t)) return;
      const errorText = err.data?.errorText;
      if (typeof errorText === 'string') {
        setError(errorText);
      } else if (typeof errorText === 'object') {
        const messages = Object.values(errorText).flat();
        setError(messages.join(' '));
      } else {
        setError(t('auth.loginFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="auth-card" style={{ maxWidth: '420px', width: '100%' }}>
      <Card.Body className="p-4 p-md-5">
        <div className="text-center mb-4">
          <div className="icon-circle bg-primary bg-gradient mx-auto mb-3">
            <i className="bi bi-box-arrow-in-right text-white"></i>
          </div>
          <h2 className="fw-bold">{t('auth.welcomeBack')}</h2>
          <p className="text-muted">{t('auth.signInToAccount')}</p>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>{t('auth.email')}</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('auth.enterEmail')}
              required
              autoComplete="email"
              size="lg"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('auth.password')}</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth.enterPassword')}
                required
                autoComplete="current-password"
                size="lg"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
              </button>
            </div>
          </Form.Group>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <Form.Check
              type="checkbox"
              name="rememberMe"
              label={t('auth.rememberMe')}
              checked={formData.rememberMe}
              onChange={handleChange}
            />
            <Button
              variant="link"
              className="p-0 link-primary"
              onClick={onSwitchToForgotPassword}
            >
              {t('auth.forgotPassword')}
            </Button>
          </div>

          <Button
            type="submit"
            className="btn-gradient w-100 py-2"
            size="lg"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('auth.signingIn')}
              </>
            ) : (
              t('auth.login')
            )}
          </Button>
        </Form>

        <div className="d-flex gap-2 mt-3">
          <Button
            variant="outline-primary"
            className="flex-grow-1"
            size="lg"
            onClick={handleAboutProjectClick}
          >
            <i className="bi bi-info-circle me-1"></i>
            {t('auth.aboutProject')}
          </Button>
        </div>

        <div className="text-center mt-4 pt-3 border-top">
          <p className="small text-muted mb-2">{t('auth.contactUs')}</p>
          <a
            href="https://t.me/flydarin"
            target="_blank"
            rel="noopener noreferrer"
            className="d-inline-flex align-items-center gap-2 text-decoration-none mb-3"
          >
            <i className="bi bi-telegram" style={{ fontSize: '1.25rem' }}></i>
            <span>{t('auth.telegramChat')}</span>
          </a>
          <div className="mt-2">
            <img src="/images/telegram-qr.png" alt="QR Telegram" width={120} height={120} className="img-fluid" />
          </div>
        </div>

        <hr className="my-4" />

        <p className="text-center mb-0">
          {t('auth.noAccount')}{' '}
          <Button variant="link" className="p-0 link-primary" onClick={onSwitchToRegister}>
            {t('auth.signUpNow')}
          </Button>
        </p>
      </Card.Body>
    </Card>
  );
};

export default Login;
